import richTextUtils from "../../utils/richtext.utils";
import FileUploaderModal from "@plugins/file-uploader-v2/components/FileUploaderModal.vue";
import MediaObjectService from "@plugins/richtext/service/media.object.service";
import i18n from "@plugins/i18n";
import MediaSettingsSubpage from "@/plugins/media/components/MediaSettingsSubpage.vue";
import DOMUtils from "@utils/dom.utils";
import {
  ADD_COMPONENT_TYPE,
  ADD_FROM_EXISTING_LOCATION,
  ADD_FROM_EXISTING_TYPE,
  ADD_FROM_LIBRARY_SEARCH_MODE,
  MEDIA_TYPE,
  NAMESPACE,
} from "@modules/editor/service/editor.constant";
import { RESOURCE_DOMAIN } from "@plugins/common/constant/resource.constant";
import { CroppingToolKind } from "@plugins/cropping/cropping.constant";
import AddFromStarredResourcesContentSubpage from "@modules/editor/components/lesson/AddFromStarredResourcesContentSubpage.vue";
import { isNull } from "@utils/var.check";
import randomUtils from "@utils/random.utils.ts";
import { FileResponse, FileStatus, UploadFlow } from "@plugins/file-uploader-v2/types.ts";
import AddMediaObjectContentSubpage from "@modules/resource-search/AddMediaObjectContentSubpage.vue";

export default function (editor) {
  const MEDIA_SELECTOR = ".sls-media[data-type='mo'], .sls-media-object";
  const richTextEditor = editor.options.get("richTextEditor");
  const context = richTextEditor.richTextContext;
  const pluginEvent = editor.options.get("pluginEvent");
  let mediaObjectService = null;
  let modalId = null;

  editor.ui.registry.addMenuButton("media_object", {
    tooltip: i18n.t("tinymce_plugin_media_add_button_tooltip"),
    icon: "file-upload",
    fetch: (callback) => {
      const items = [
        {
          type: "menuitem",
          text: i18n.t("tinymce_plugin_media_add_from_library_button_tooltip"),
          onAction: () => {
            openAddFromLibrary();
          },
        },
        {
          type: "menuitem",
          text: i18n.t("tinymce_plugin_media_add_from_mydrive_button_tooltip"),
          onAction: () => {
            openAddFromMyDrive();
          },
        },
        {
          type: "menuitem",
          text: i18n.t("tinymce_plugin_media_add_from_starred_resources_button_tooltip"),
          onAction: () => {
            openAddFromStarredResources();
          },
        },
        {
          type: "menuitem",
          text: i18n.t("tinymce_plugin_media_upload_button_tooltip"),
          onAction: () => {
            openModal();
          },
        },
      ];
      callback(items);
    },
  });

  editor.addCommand("mceMediaDelete", (targetNode) => {
    richTextUtils.removeNodeFromEditor(editor, targetNode);
  });

  editor.on("click", (e) => {
    const target = e.target;
    if (target.closest("button.trash") && target.closest("button.trash").length !== 0) {
      const targetNode = target.closest(".sls-media-wrapper");
      //to go throught execCommand, so that tinymce can control the delete event
      if (targetNode) editor.execCommand("mceMediaDelete", targetNode);
    }

    // cater for cases when have undo action
    handlePluginRerender(target);
  });

  (function init() {
    richTextEditor.plugins["mediaObject"] = {
      renderInEditor,
      cleanUp,
      containPluginElements,
      openModal,
      openSettings,
    };
    mediaObjectService = new MediaObjectService(context);
  })();

  function openModal() {
    modalId = richTextEditor.$modal.show(
      FileUploaderModal,
      {
        uploadFlow: UploadFlow.ANY_FILE,
        croppingConfiguration: {
          kind: CroppingToolKind.cropping_tool_4.key,
        },
        modalId: randomUtils.randomId(),
      },
      {
        "update:fileEntry": (fileResponse: FileResponse) => {
          handleFileUpload(fileResponse);
        },
      },
      true,
    );
  }

  function openAddFromLibrary() {
    const defaultAnswer = richTextEditor.$el.classList.contains("question-default-answer");
    const contentSubpageParams = {
      baseTitle: [
        richTextEditor.store.getters[`${NAMESPACE.LESSON}/getLessonTitle`],
        i18n.t("add_from_library_label"),
      ],
      restrictMediaType: [] as string[],
      restrictLocation: defaultAnswer
        ? [RESOURCE_DOMAIN.MOE]
        : [RESOURCE_DOMAIN.COMMUNITY, RESOURCE_DOMAIN.MOE],
      showKeywordSearchFilterAccordion: true,
      checkCopyRightForMOEResource: !!defaultAnswer,
      fromMoeLibrary:
        richTextEditor.store.getters[`${NAMESPACE.LESSON}/resourceDomain`] === "moe-library",
    };

    richTextEditor.$contentSubpage.show(AddMediaObjectContentSubpage, contentSubpageParams, {
      selected: handleAddedMediaFromLibrary,
    });
  }

  function openAddFromMyDrive() {
    const defaultAnswer = richTextEditor.$el.classList.contains("question-default-answer");
    const contentSubpageParams = {
      baseTitle: [
        richTextEditor.store.getters[`${NAMESPACE.LESSON}/getLessonTitle`],
        i18n.t("add_from_my_drive_label"),
      ],
      restrictMediaType: [] as string[],
      restrictLocation: [RESOURCE_DOMAIN.PERSONAL],
      showKeywordSearchFilterAccordion: true,
      checkCopyRightForMOEResource: !!defaultAnswer,
      fromMoeLibrary:
        richTextEditor.store.getters[`${NAMESPACE.LESSON}/resourceDomain`] === "moe-library",
    };

    richTextEditor.$contentSubpage.show(AddMediaObjectContentSubpage, contentSubpageParams, {
      selected: handleAddedMediaFromLibrary,
    });
  }

  function openAddFromStarredResources() {
    const defaultAnswer = richTextEditor.$el.classList.contains("question-default-answer");
    const mediaTypes = richTextEditor?.config?.plugins?.mediaObject?.addFromStarredResources
      ?.mediaTypes || [
      MEDIA_TYPE.IMAGE,
      MEDIA_TYPE.AUDIO,
      MEDIA_TYPE.VIDEO,
      MEDIA_TYPE.HTML5,
      MEDIA_TYPE.POWERPOINT,
      MEDIA_TYPE.WORD,
      MEDIA_TYPE.EXCEL,
      MEDIA_TYPE.PDF,
      MEDIA_TYPE.OTHERS,
      MEDIA_TYPE.ZIP,
    ];
    let copyRightForMOEResource = false;
    // Prepopulated answer will only accept the following filters:
    // 1. For MOE resource, modification flag is set to TRUE
    if (defaultAnswer) {
      copyRightForMOEResource = true;
    }
    richTextEditor.$contentSubpage.show(
      AddFromStarredResourcesContentSubpage,
      {
        resourceType: ADD_COMPONENT_TYPE.MEDIA,
        mediaTypes: mediaTypes,
        copyRightForMOEResource: copyRightForMOEResource,
        mode: ADD_FROM_LIBRARY_SEARCH_MODE.SINGLE_SELECTION,
        addFromType: ADD_FROM_EXISTING_TYPE.MEDIA,
        addFromLocation: ADD_FROM_EXISTING_LOCATION.STARRED_RESOURCES,
        allowUpload: richTextEditor.allowUpload,
        subpageParentTitle: richTextEditor.subpageParentTitle,
      },
      {
        onMediaSelected: handleAddedMediaFromLibraryStarred,
      },
    );
  }

  async function handleAddedMediaFromLibrary({ data, onSuccess }) {
    if (!isNull(data.description) && data.description.includes("sls-tts")) {
      // Handle text-to-speech
      richTextUtils.createInlineTextToSpeechAttachment(
        editor,
        {
          audioUuid: data.uuid,
          audioText: richTextUtils.stripHTML(data.description),
        },
        { onTextToSpeechSettings: openSettings },
      );
    } else {
      if (
        data.objectType === MEDIA_TYPE.IMAGE &&
        data.resourceDomain !== RESOURCE_DOMAIN.MOE &&
        data.resourceDomain !== RESOURCE_DOMAIN.COMMUNITY
      ) {
        const blob = await mediaObjectService.getImageBlob(data.fileUrl);
        const fileExt = data.fileUrl.split(/[#?]/)[0].split(".").pop().trim();
        const file = new File([blob], `${data.title}.${fileExt}`, {
          type: blob.type,
        });

        modalId = richTextEditor.$modal.show(
          FileUploaderModal,
          {
            uploadFlow: UploadFlow.IMG,
            addedFile: file,
            croppingConfiguration: {
              kind: CroppingToolKind.cropping_tool_3.key,
            },
          },
          {
            "update:fileEntry": (uploadedResult: FileResponse) => {
              if (uploadedResult?.status === FileStatus.READY) {
                handleFileUpload(uploadedResult, data.uuid);
              }
            },
          },
          true,
        );
      } else {
        richTextUtils.insertInlineAttachment(editor, {
          uuid: data.uuid,
          mediaType: data.objectType,
          fileName: data.title,
          resourceDomain: data.resourceDomain,
          type: mediaObjectService.getType(),
          isAddNewMedia: true,
        });

        // to cater HTML5 is added from starred resource, to populate the html5 mo uuid
        if (data.objectType === MEDIA_TYPE.HTML5 && richTextEditor.plugins?.["html5_interactive"]) {
          richTextEditor.plugins["html5_interactive"].initializeHTML5MOUuid(data.uuid);
          richTextEditor.$emit("changeHtml5Interactive", data.uuid);
        }

        richTextUtils.renderInlineMedia(editor, ".inline-placeholder", {
          onMediaSettings: openSettings,
          onOpenPdfInNewTab: openPdfInNewTab,
        });
      }

      editor.save();
    }
    onSuccess();
  }

  async function handleAddedMediaFromLibraryStarred(data) {
    if (!isNull(data[0].description) && data[0].description.includes("sls-tts")) {
      // Handle text-to-speech
      richTextUtils.createInlineTextToSpeechAttachment(
        editor,
        {
          audioUuid: data[0].uuid,
          audioText: richTextUtils.stripHTML(data[0].description),
        },
        { onTextToSpeechSettings: openSettings },
      );
    } else {
      if (
        data[0].type === MEDIA_TYPE.IMAGE &&
        data[0].resourceDomain !== RESOURCE_DOMAIN.MOE &&
        data[0].resourceDomain !== RESOURCE_DOMAIN.COMMUNITY
      ) {
        const blob = await mediaObjectService.getImageBlob(data[0].dataSrc);
        const fileExt = data[0].dataSrc.split(/[#?]/)[0].split(".").pop().trim();
        const file = new File([blob], `${data[0].title}.${fileExt}`, {
          type: blob.type,
        });

        modalId = richTextEditor.$modal.show(
          FileUploaderModal,
          {
            uploadFlow: UploadFlow.IMG,
            addedFile: file,
            croppingConfiguration: {
              kind: CroppingToolKind.cropping_tool_3.key,
            },
          },
          {
            "update:fileEntry": (uploadedResult: FileResponse) => {
              if (uploadedResult?.status === FileStatus.READY) {
                handleFileUpload(uploadedResult, data[0].uuid);
              }
            },
          },
          true,
        );
      } else {
        richTextUtils.insertInlineAttachment(editor, {
          uuid: data[0].uuid,
          mediaType: data[0].type,
          fileName: data[0].title,
          resourceDomain: data[0].resourceDomain,
          type: mediaObjectService.getType(),
          isAddNewMedia: data[0].isAddNewMedia,
        });

        // to cater HTML5 is added from starred resource, to populate the html5 mo uuid
        if (data[0].type === MEDIA_TYPE.HTML5 && richTextEditor.plugins?.["html5_interactive"]) {
          richTextEditor.plugins["html5_interactive"].initializeHTML5MOUuid(data[0].uuid);
          richTextEditor.$emit("changeHtml5Interactive", data[0].uuid);
        }

        richTextUtils.renderInlineMedia(editor, ".inline-placeholder", {
          onMediaSettings: openSettings,
          onOpenPdfInNewTab: openPdfInNewTab,
        });
      }

      editor.save();
    }
  }

  function openSettings(settings) {
    window.tinymce.currentEditor = editor;
    settings.editorSaveCallback = editorSaveCallback;
    settings.isHTML5Interactive = !!richTextEditor.html5MOUuid;
    richTextEditor.$contentSubpage.show(MediaSettingsSubpage, settings);
  }

  function openPdfInNewTab(uuid) {
    const routeData = richTextEditor.$router.resolve({
      name: "PdfView",
      params: { uuid: uuid },
    });
    window.open(routeData.href, "_blank");
  }

  async function handleFileUpload(
    fileResponse: FileResponse,
    sourceMediaObjectUuid?: string,
  ): Promise<void> {
    const data: { uuid: string; type?: string; fileType?: string } =
      await mediaObjectService.create(fileResponse, false, sourceMediaObjectUuid);
    richTextEditor.$modal.remove(modalId);
    richTextUtils.insertInlineAttachment(editor, {
      uuid: data.uuid,
      mediaType: data.type || data.fileType,
      fileName: fileResponse.name,
      type: mediaObjectService.getType(),
      isAddNewMedia: true,
    });
    richTextUtils.renderInlineMedia(editor, ".inline-placeholder", {
      onMediaSettings: openSettings,
      onOpenPdfInNewTab: openPdfInNewTab,
    });
    editor.save();
    if (pluginEvent) pluginEvent.$emit("mediaRendered");
  }

  function renderInEditor(rerender = false) {
    // convert any legacy objects
    richTextUtils.convertLegacyMediaObjects(editor.getBody());
    richTextUtils.convertLegacyImageMediaObjects(editor.getBody());
    richTextUtils.renderInlineMedia(
      editor,
      MEDIA_SELECTOR,
      {
        onMediaSettings: openSettings,
        onOpenPdfInNewTab: openPdfInNewTab,
      },
      rerender,
    );
  }

  function editorSaveCallback() {
    if (window.tinymce && window.tinymce.currentEditor) {
      window.tinymce.currentEditor.save();
      delete window.tinymce.currentEditor;
    }
  }

  function cleanUp(content) {
    const container = document.createElement("div");
    const containerAfterCleanUp = document.createElement("div");
    container.innerHTML = content;
    containerAfterCleanUp.innerHTML = content;

    // remove placeholder
    DOMUtils.elOf(containerAfterCleanUp, ".sls-media-wrapper").forEach((element) => {
      if (DOMUtils.elOf(element, ".inline-placeholder").length !== 0) {
        element.remove();
      }
    });
    DOMUtils.elOf(containerAfterCleanUp, ".inline-placeholder").forEach((element) => {
      element.remove();
    });
    DOMUtils.elOf(container, ".sls-media-tag[data-type='mo']").forEach((element) => {
      cleanUpElement(containerAfterCleanUp, element);
    });

    DOMUtils.elOf(containerAfterCleanUp, "br[data-mce-bogus]").forEach((element) => {
      element.remove();
    });

    return containerAfterCleanUp.innerHTML;
  }

  function cleanUpElement(container, element) {
    const uuid = element.getAttribute("data-uuid");
    const media = DOMUtils.elOf(container, `.sls-media-tag[data-uuid='${uuid}']`)[0];
    const mediaWrapper = media.closest(".sls-media-wrapper");

    // switch back the class
    DOMUtils.removeClass(element, "sls-media-tag");
    DOMUtils.addClass(element, "sls-media");
    mediaWrapper.replaceWith(element);
  }

  function containPluginElements(container) {
    return container.querySelectorAll(".sls-media, .sls-media-object, figure.media").length > 0;
  }

  function handlePluginRerender(target) {
    let mediaComponent = target.closest(".media-component");
    if (!mediaComponent || mediaComponent.length === 0 || mediaComponent.__vue__) return;

    const targetNode = mediaComponent.closest(".sls-media-wrapper")
      ? DOMUtils.elOf(
          mediaComponent.closest(".sls-media-wrapper"),
          ".sls-media-tag[data-type='mo']",
        )[0]
      : null;
    if (!targetNode) return;

    const parentMediaComponent = mediaComponent.closest(".paragraph");
    const targetComponentIndex = DOMUtils.elOf(parentMediaComponent, ".media-component").indexOf(
      mediaComponent,
    );

    cleanUpElement(editor.getBody(), targetNode);
    renderInEditor(true);

    mediaComponent = DOMUtils.elOf(parentMediaComponent, ".media-component")[targetComponentIndex];
    const mediaVueComponent = mediaComponent.__vue__;
    if (mediaVueComponent) {
      // enable drawing plugin
      if (pluginEvent) pluginEvent.$emit("mediaRendered");
    }
  }
}
